import { CSSProperties, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    AccessRoleDTO,
    AccessTeamDTO,
    AccessTeamRoleDTO,
    AccessTeamUserDTO,
    useToggleAssignUserTeam,
    useSearchTeamsApi,
    useDeleteRoleTeamsApi,
} from '../../api/role-teams-api';
import SvgAdd from '../../assets/icons/Add';
import { InitFailed } from '../../components/basic/init-failed';
import { Loading } from '../../components/basic/loading';
import { notify } from '../../components/basic/notify';
import { T, translate } from '../../components/basic/text';
import { SectionTitle } from '../../components/ui/section-title';
import { TProvider } from '../../i18n';
import { IBasicTableColumn } from '../../interfaces/interfaces';
import { ManageTeamModal } from './manage-team-modal';
import { SelectUser } from '../../components/user/select-user';
import SvgDelete from '../../assets/icons/Delete';

export const RoleTeams = () => {
    const MaxUsersCountDisplayInTeam = 10;
    const [teamUsers, setTeamUsers] = useState<{
        modalShow: boolean;
        users: AccessTeamUserDTO[];
        readonly: boolean;
        team?: AccessTeamDTO;
    }>({
        modalShow: false,
        users: [],
        readonly: true,
    });
    const [handleRoleTeamModal, setHandleRoleTeamModal] = useState<{
        team?: AccessTeamDTO;
        roles: AccessRoleDTO[];
        teamRoles: AccessTeamRoleDTO[];
        teamUsers: AccessTeamUserDTO[];
        show: boolean;
    }>({ show: false, roles: [], teamUsers: [], teamRoles: [] });

    const searchTeamsApi = useSearchTeamsApi();
    const deleteRoleTeamsApi = useDeleteRoleTeamsApi();

    const toggleAssignUserTeam = useToggleAssignUserTeam();

    const allowEdit = searchTeamsApi?.result?.allowEdit;

    const getUsers = (team: AccessTeamDTO) => {
        if (!searchTeamsApi.result) return [];
        return searchTeamsApi.result.teamUsers?.filter(x => x.accessTeamSerial === team.serial);
    };

    const isReadOnly = (team: AccessTeamDTO) =>
        !!team && !team.isActive && (!searchTeamsApi.result || allowEdit);

    const deleteUserHandler = (user: AccessTeamUserDTO) => {
        bd.msgbox(
            <T>deleting-selected-user</T>,
            user.userName + ' | ' + user.displayName,

            [
                {
                    title: <T>delete</T>,
                    variant: 'contained',
                    action: hide => {
                        toggleAssignUserTeam.post(
                            null,
                            {
                                userName: user.userName,
                                teamSerial: user.accessTeamSerial,
                            },
                            result => searchTeamsApi.updateResult(result)
                        );

                        hide();
                    },
                },
                { title: <T>close</T> },
            ],
            { backdropClassName: 'modal-n', className: 'modal-n' }
        );
    };

    const addUserHandler = (teamSerial: number, userName: string) => {
        if (
            searchTeamsApi.result.teamUsers.some(
                x => x.accessTeamSerial === teamSerial && x.userName === userName
            )
        ) {
            notify.error(translate('user-already-assigned-to-team'));
        } else {
            toggleAssignUserTeam
                .callApi(null, { userName, teamSerial })
                .then(result => searchTeamsApi.updateResult(result));
        }
    };

    const deleteTeamHandler = (team: AccessTeamDTO) => {
        bd.msgbox(
            <T>deleting-selected-team</T>,
            team.title,

            [
                {
                    title: <T>delete</T>,
                    variant: 'contained',
                    action: hide => {
                        deleteRoleTeamsApi.callApi(team).then(result => {
                            searchTeamsApi.updateResult({
                                ...searchTeamsApi.result,
                                teams: result.teams,
                            });
                        });
                        hide();
                    },
                },
                { title: <T>close</T> },
            ],
            { backdropClassName: 'modal-n', className: 'modal-n' }
        );
    };

    const columns: IBasicTableColumn<AccessTeamDTO>[] = useMemo(
        () => [
            {
                Header: <T>role-team-title</T>,
                name: 'title',
                Cell: ({ row }) => (
                    <bd.Flex align="center" gap={2}>
                        <span
                            className="text-link"
                            onClick={() => {
                                setHandleRoleTeamModal({
                                    show: true,
                                    roles: searchTeamsApi.result?.roles || [],
                                    teamUsers: searchTeamsApi.result?.teamUsers || [],
                                    teamRoles: searchTeamsApi.result?.teamRoles || [],
                                    team: row,
                                });
                            }}
                        >
                            {row.title}
                        </span>
                        {!row.isActive && (
                            <bd.Chip
                                size="sm"
                                className="border border-danger bg-danger text-danger "
                                label={translate('disabled')}
                            />
                        )}
                    </bd.Flex>
                ),
                width: 200,
            },
            {
                Header: <T>users</T>,
                name: 'serial',
                Cell: ({ row }) => {
                    const team_users = getUsers(row);
                    const is_readonly = isReadOnly(row);
                    const style: CSSProperties = { opacity: is_readonly ? 0.75 : 1 };
                    return (
                        <bd.Flex className="my-2" align="center" gap={2} wrap>
                            {team_users.slice(0, MaxUsersCountDisplayInTeam).map(u => (
                                <bd.Chip
                                    size="sm"
                                    outline
                                    onDelete={
                                        !allowEdit || is_readonly
                                            ? undefined
                                            : () => deleteUserHandler(u)
                                    }
                                    className={is_readonly ? 'bg-shade-10 text-muted' : 'bg-gray-4'}
                                    style={style}
                                    deleteColor="secondary"
                                    label={u.displayName}
                                />
                            ))}
                            {team_users.length > MaxUsersCountDisplayInTeam && (
                                <bd.Button
                                    size="sm"
                                    variant="text"
                                    onClick={() =>
                                        setTeamUsers({
                                            modalShow: true,
                                            users: team_users,
                                            readonly: is_readonly,
                                            team: row,
                                        })
                                    }
                                >
                                    <T>view-more</T>
                                </bd.Button>
                            )}
                            {allowEdit && !isReadOnly(row) && (
                                <SelectUser
                                    onChange={({ userName }) =>
                                        addUserHandler(row.serial, userName)
                                    }
                                />
                            )}
                            {allowEdit && team_users.length === 0 && !isReadOnly(row) && (
                                <bd.Button
                                    size="sm"
                                    variant=""
                                    color="secondary"
                                    onClick={() => deleteTeamHandler(row)}
                                >
                                    <SvgDelete />
                                    <T>delete-team</T>...
                                </bd.Button>
                            )}
                        </bd.Flex>
                    );
                },
                //     (
                //     <span
                //         className="text-link"
                //         onClick={() => {
                //             setHandleRoleTeamModal({
                //                 show: true,
                //                 roles: searchTeamsApi.result?.roles || [],
                //                 teamUsers: searchTeamsApi.result?.teamUsers || [],
                //                 team: row,
                //             });
                //         }}>
                //         {row.title}
                //     </span>
                // ),
            },
        ],
        [searchTeamsApi.result]
    );

    useEffect(() => {
        if (searchTeamsApi.initialized || searchTeamsApi.initializing) return;
        searchTeamsApi.post();
    }, []);

    const CloseModal = () =>
        setHandleRoleTeamModal({ ...handleRoleTeamModal, show: false, team: undefined });

    if (searchTeamsApi.initializing) return <Loading />;
    if (searchTeamsApi.error)
        return <InitFailed ex={searchTeamsApi.error} onRetry={searchTeamsApi.post} />;

    return (
        <TProvider>
            <div className="p-2">
                <SectionTitle
                    // title="team-groups"
                    hideBorder
                    controls={
                        <bd.Button
                            className="min-w-80"
                            size="sm"
                            variant="outline"
                            disabled={!allowEdit}
                            onClick={() =>
                                setHandleRoleTeamModal({
                                    show: true,
                                    roles: searchTeamsApi.result?.roles || [],
                                    teamUsers: searchTeamsApi.result?.teamUsers || [],
                                    teamRoles: searchTeamsApi.result?.teamRoles || [],
                                })
                            }
                        >
                            <SvgAdd />
                            <T>new-accessTeam</T>
                        </bd.Button>
                    }
                />
                <bd.BasicTable
                    className="mt-2 mx-n2 mb-n2"
                    columns={columns}
                    data={searchTeamsApi.result?.teams ?? []}
                    cellCSS={({ row }: any) => {
                        if (!row) return '';

                        if (!row.isActive) return 'opacity-50 bg-gray-5 text-muted';
                        return '';
                    }}
                    hover
                />
                <ManageTeamModal
                    show={handleRoleTeamModal.show}
                    team={handleRoleTeamModal.team}
                    roles={handleRoleTeamModal.roles}
                    teamRoles={handleRoleTeamModal.teamRoles}
                    teamUsers={handleRoleTeamModal.teamUsers}
                    allowEdit={!!allowEdit}
                    onHide={CloseModal}
                    onSave={result => {
                        const updateMode =
                            handleRoleTeamModal.team && handleRoleTeamModal.team.serial > 0;

                        searchTeamsApi.updateResult({
                            ...searchTeamsApi.result,
                            teams: result.teams,
                            teamRoles: result.teamRoles,
                        });

                        CloseModal();
                        notify.success(
                            translate(updateMode ? 'roleTeam-updated' : 'roleTeam-created')
                        );
                    }}
                    onDeleted={() => {
                        if (!handleRoleTeamModal.team || !searchTeamsApi.result) return;
                        const team = handleRoleTeamModal.team;
                        // searchRoleTeamsApi.updateResult({
                        //     teams: searchRoleTeamsApi.result.teams.filter((x) => x.serial !== roleTeam.serial),
                        // });

                        notify.dark(translate('roleTeam-deleted'));
                        CloseModal();
                    }}
                />
            </div>
        </TProvider>
    );
};
